import { memo, useEffect, useMemo } from "react";
import {
  Modal,
  Header,
  Icon,
  Divider,
  Form,
  FormGroup,
  FormField,
  Dropdown,
  Button,
} from "semantic-ui-react";

// Components
import FormViewBox from "./FormViewBox";

// Commons
import { DateTextBox } from "../common";
import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";

// Serializers
import DiagFormSerializer from "issara-sdk/types/DiagFormSerializer_apps_PRX";
import DiagRuleSerializer from "issara-sdk/types/DiagRuleSerializer_apps_PRX";

// Types
import { DiagFormFilter } from "./StaffChatInterface";
import { OptionType } from "../HISV3/common/CommonInterface";
import { FormModal, FormTab } from "./FormSection";
import { AllOption, FORM_TYPE_OPTIONS } from "../MeForm/sequence/MeForm";

// CSS
import './FormSectionModal.scss';

type FormSectionModalProps = {
  open: boolean;
  activeTab: FormTab;
  filter: DiagFormFilter;
  formModal: FormModal;
  selectedForm: DiagFormSerializer | null;
  // -- pagination
  diagFormList: DiagRuleSerializer[];
  formItems: DiagFormSerializer[];
  searchLoading: boolean;
  activePage: number;
  total: number;
  // -- callback
  onClose: () => void;
  onChangeTab: (tab: FormTab) => void;
  onChangeFilter: <K extends keyof DiagFormFilter>(
    key: K,
    value: DiagFormFilter[K],
    isView?: boolean
  ) => void;
  onSearch: (activePage: number) => void;
  onSelectForm: (form: DiagFormSerializer) => void;
};

const FormSectionModal = (props: FormSectionModalProps) => {
  useEffect(() => {
    if (props.formModal.open && props.formModal.mode === "view") {
      props.onChangeFilter("diag_rule", props.formModal.id);
    }
  }, [props.formModal]);

  const formOptions = useMemo(() => {
    const items = props.diagFormList ?? [];

    const options = items
      .filter((item) => {
        if (props.filter.form_type === "ALL") return true;

        return item.form_type === props.filter.form_type;
      })
      .map((item) => ({
        key: item.id,
        value: item.id,
        text: item.name,
      }));

    return [AllOption, ...options] as OptionType[];
  }, [props.diagFormList, props.filter.form_type]);

  const categoryOptions = useMemo(() => {
    const formTypeList = FORM_TYPE_OPTIONS;

    if (!formTypeList.length) return [AllOption] as OptionType[];

    const options = formTypeList.map((type) => ({
      key: type.key,
      value: type.value,
      text: type.text,
    }));

    return [AllOption, ...options] as OptionType[];
  }, []);

  useEffect(() => {
    if (
      props.filter.diag_rule !== "ALL" &&
      !formOptions.some((opt) => opt.value === props.filter.diag_rule)
    ) {
      props.onChangeFilter("diag_rule", "ALL");
    }
  }, [formOptions]);

  const handleDiagRuleChangeView = (e: any, data: any) => {
    const prevValue = Number(props.filter?.diag_rule);
    const nextValues = data.value as number[];

    const selected = nextValues.find((v) => v !== prevValue);

    if (selected !== undefined) {
      props.onChangeFilter("diag_rule", selected, true);
    } else {
      props.onChangeFilter("diag_rule", "ALL", true);
    }
  };

  const handleDiagRuleChangeSingle = (e: any, data: any) => {
    props.onChangeFilter("diag_rule", data.value as "ALL" | number);
  };

  return (
    <Modal id="FormSectionModal" size="large" open={props.open} onClose={props.onClose} style={{ padding: "1rem" }}>
      <div>
        {/* --- Header --- */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Header as="h3" style={{ margin: 0 }}>
            ประวัติการทำแบบคัดกรอง
          </Header>
          <Icon
            name="close"
            color="red"
            size="large"
            onClick={props.onClose}
            style={{ cursor: "pointer" }}
          />
        </div>

        <Divider style={{ margin: "0.5rem 0" }} />

        {/* --- Filter --- */}
        <Form>
          <FormGroup inline>
            <FormField>
              <label>ประเภทแบบประเมิน</label>
            </FormField>
            <FormField width={3}>
              <div style={{ width: "100%" }}>
                <Dropdown
                  value={props.filter?.form_type ?? undefined}
                  fluid
                  selection
                  options={categoryOptions || []}
                  onChange={(e, data) =>
                    props.onChangeFilter("form_type", data.value as DiagFormFilter["form_type"])
                  }
                />
              </div>
            </FormField>
            <FormField>
              <label>ชื่อแบบประเมิน</label>
            </FormField>
            <FormField width={3}>
              <div style={{ width: "100%" }}>
                <Dropdown
                  value={
                    props.filter?.type === "view"
                      ? [Number(props.filter?.diag_rule)]
                      : props.filter?.diag_rule === "ALL"
                      ? "ALL"
                      : Number(props.filter?.diag_rule)
                  }
                  fluid
                  selection
                  multiple={props.filter?.type === "view"} // multiple เฉพาะ view
                  options={formOptions || []}
                  closeOnChange={true}
                  onChange={
                    props.filter?.type === "view"
                      ? handleDiagRuleChangeView
                      : handleDiagRuleChangeSingle
                  }
                />
              </div>
            </FormField>
            <FormField>
              <label>วันที่ทำแบบประเมิน</label>
            </FormField>
            <FormField width={3}>
              <div style={{ width: "100%" }}>
                <DateTextBox
                  value={props.filter?.start_date ?? ""}
                  inputFluid
                  onChange={(value) => props.onChangeFilter("start_date", value)}
                />
              </div>
            </FormField>
            <FormField>
              <label>ถึง</label>
            </FormField>
            <FormField width={3}>
              <div style={{ width: "100%" }}>
                <DateTextBox
                  value={props.filter?.end_date ?? ""}
                  inputFluid
                  onChange={(value) => props.onChangeFilter("end_date", value)}
                />
              </div>
            </FormField>
            <FormField>
              <div style={{ width: "100%" }}>
                <Button
                  loading={props.searchLoading}
                  fluid
                  children={"ค้นหา"}
                  color="blue"
                  onClick={() => props.onSearch(props.activePage)}
                />
              </div>
            </FormField>
          </FormGroup>

          <FormGroup inline>
            <FormField width={2}>
              <div style={{ width: "100%" }}>
                <Button
                  fluid
                  children={"Overview"}
                  onClick={() => props.onChangeTab("overview")}
                  style={{
                    backgroundColor:
                      props.activeTab === "overview" ? "var(--primary-theme-color)" : undefined,
                    color: props.activeTab === "overview" ? "white" : undefined,
                  }}
                />
              </div>
            </FormField>
            <FormField width={2}>
              <div style={{ width: "100%" }}>
                <Button
                  fluid
                  children={"History"}
                  onClick={() => props.onChangeTab("history")}
                  style={{
                    backgroundColor:
                      props.activeTab === "history" ? "var(--primary-theme-color)" : undefined,
                    color: props.activeTab === "history" ? "white" : undefined,
                  }}
                />
              </div>
            </FormField>
          </FormGroup>
        </Form>

        <Divider style={{ margin: "0.5rem 0" }} />

        {/* Form Overview/History Component */}
        <FormViewBox
          tab={props.activeTab}
          selectedForm={props.selectedForm}
          // -- pagination
          formItems={props.formItems}
          activePage={props.activePage}
          total={props.total}
          // -- callback
          onSelectForm={props.onSelectForm}
          onChangePage={props.onSearch}
        />
      </div>
    </Modal>
  );
};

FormSectionModal.displayName = "FormSectionModal";

export default memo(FormSectionModal);
